import type { Content } from '../types/content'

export const es: Content = {
  documentTitle: 'Standard Consulting Advice | Consultoría Empresarial, Fiscal y Financiera en EE. UU.',
  gate: {
    title: 'Elige Tu Idioma',
    subtitle: 'Selecciona tu idioma preferido para continuar.',
    english: 'English',
    englishNote: 'Continue in English',
    spanish: 'Español',
    spanishNote: 'Continuar en español',
    footnote: 'Puedes cambiar el idioma en cualquier momento desde el encabezado.',
  },
  switcher: {
    label: 'Cambiar idioma',
    en: 'EN',
    es: 'ES',
  },
  nav: {
    links: [
      { label: 'Servicios', href: '#services' },
      { label: 'Por Qué Nosotros', href: '#why-us' },
      { label: 'Proceso', href: '#process' },
      { label: 'Testimonios', href: '#testimonials' },
      { label: 'Preguntas', href: '#faq' },
    ],
    offices: {
      label: 'Nuestras Oficinas',
      countries: [
        { name: 'Estados Unidos', flag: '🇺🇸', href: '#top', external: false },
        { name: 'Colombia', flag: '🇨🇴', href: 'https://standardconsulting.co/', external: true },
        { name: 'Honduras', flag: '🇭🇳', href: 'https://www.cascocarrasco.com', external: true },
      ],
    },
    phone: '+1 (305) 555-0142',
    cta: 'Agendar Consulta',
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    mobileCta: 'Agendar Mi Consulta Gratuita',
    ariaLabel: 'Principal',
    homeLabel: 'Standard Consulting Advice — inicio',
  },
  hero: {
    badge: 'Con la confianza de emprendedores e inversionistas',
    headline: 'Expande Tu Negocio a los Estados Unidos',
    headlineAccent: 'Sin Complicaciones',
    subtitle:
      'Ayudamos a emprendedores e inversionistas a establecer, estructurar y hacer crecer empresas en los Estados Unidos mediante consultoría legal, financiera y fiscal.',
    primaryCta: 'Agendar Mi Consulta Gratuita',
    secondaryCta: 'Conocer Más',
    imageAlt: 'Equipo ejecutivo de asesoría reuniéndose en una oficina moderna de altura',
    statLabel: 'Tiempo promedio',
    statValue: '24–72 hrs',
    statBody: 'Desde la contratación hasta tu empresa registrada en EE. UU.',
    trustIndicators: [
      'Proceso 100% Remoto',
      'Especialistas en EE. UU.',
      'Respuesta Rápida',
      'Consultoría Personalizada',
      'Oficinas en Florida y Nueva York',
    ],
  },
  socialProof: {
    eyebrow: 'Asesorando a fundadores e inversionistas desde 2005',
    sectionLabel: 'Resultados de la firma',
    metrics: [
      { value: '500+', label: 'Empresas Asesoradas' },
      { value: '20+', label: 'Años de Experiencia' },
      { value: '100%', label: 'Proceso Remoto' },
      { value: 'EE. UU. + LATAM', label: 'Clientes Internacionales' },
    ],
  },
  pain: {
    heading: 'Administrar un Negocio No Debería Ser Complicado',
    cards: [
      {
        title: '¿No sabes cómo crear una LLC?',
        body: 'Elección del estado, acuerdos operativos, agente registrado — un paso mal dado retrasa todo por meses.',
      },
      {
        title: '¿Dudas sobre los impuestos?',
        body: 'Las declaraciones federales, estatales e internacionales tienen fechas límite, y el IRS no envía recordatorios.',
      },
      {
        title: '¿Necesitas contabilidad y cumplimiento?',
        body: 'La contabilidad, la nómina y los informes anuales se acumulan rápido y ponen en riesgo tu empresa.',
      },
    ],
    bottom: 'Nosotros nos encargamos de todo mientras tú te enfocas en hacer crecer tu negocio.',
    cta: 'Hablar con un Experto',
  },
  services: {
    eyebrow: 'Servicios',
    heading: 'Todo lo Que Tu Negocio Necesita en un Solo Lugar',
    subtitle:
      'Cuatro áreas de práctica y un solo equipo responsable — para que nada se pierda entre tu abogado, tu contador y tu banco.',
    groups: [
      {
        title: 'Constitución de Empresas',
        description: 'Todo lo necesario para operar una entidad estadounidense en regla desde el primer día.',
        icon: 'building',
        items: ['LLC', 'Corporación', 'EIN', 'Cuenta Bancaria Empresarial', 'Agente Registrado'],
      },
      {
        title: 'Protección de Activos',
        description: 'Estructuras que separan tu patrimonio del riesgo operativo.',
        icon: 'shield',
        items: [
          'Holdings',
          'Fideicomisos',
          'Estructuras Legales',
          'Planificación Patrimonial',
          'Optimización Fiscal',
        ],
      },
      {
        title: 'Contabilidad y Nómina',
        description: 'Un back office completo gestionado por especialistas en EE. UU., mes a mes.',
        icon: 'calculator',
        items: [
          'Contabilidad',
          'Nómina',
          'Informes Financieros',
          'Cumplimiento',
          'Tercerización',
        ],
      },
      {
        title: 'Servicios Fiscales',
        description: 'Planificación y declaraciones con la disciplina de las Big Four.',
        icon: 'receipt',
        items: [
          'Impuestos Personales',
          'Impuestos Corporativos',
          'Cumplimiento del IRS',
          'ITIN',
          'Planificación Fiscal',
        ],
      },
    ],
  },
  whyUs: {
    eyebrow: 'Por qué nosotros',
    heading: 'Por Qué las Empresas Eligen Standard Consulting Advice',
    items: [
      {
        title: 'Experiencia Internacional',
        body: 'Dos décadas asesorando a fundadores e inversionistas en Estados Unidos y América Latina.',
      },
      {
        title: 'Atención Personalizada',
        body: 'Sin filas de tickets. Hablas directamente con el asesor que conoce tu estructura.',
      },
      {
        title: 'Estándares Big Four',
        body: 'Metodología, documentación y controles al nivel de las firmas globales más exigentes.',
      },
      {
        title: 'Procesos Rápidos',
        body: 'Constitución, EIN y banca avanzan en paralelo, no uno detrás del otro.',
      },
      {
        title: 'Asesores Dedicados',
        body: 'Un contacto directo para tus preguntas legales, financieras y fiscales todo el año.',
      },
      {
        title: 'Relación a Largo Plazo',
        body: 'Seguimos contigo después de la constitución — en el crecimiento y cada temporada fiscal.',
      },
    ],
  },
  process: {
    eyebrow: 'Proceso',
    heading: 'Un Camino Claro Desde la Primera Llamada Hasta el Soporte Continuo',
    steps: [
      {
        step: 'PASO 1',
        title: 'Agenda una Consulta Gratuita',
        body: 'Una llamada de 30 minutos para entender tus objetivos, plazos y estructura actual.',
      },
      {
        step: 'PASO 2',
        title: 'Evaluación del Negocio',
        body: 'Revisamos tu operación, residencia, modelo de ingresos y exposición fiscal.',
      },
      {
        step: 'PASO 3',
        title: 'Plan Estratégico',
        body: 'Recibes una recomendación por escrito: entidad, estado, estructura y costos.',
      },
      {
        step: 'PASO 4',
        title: 'Implementación',
        body: 'Registro, EIN, banca y presentaciones de cumplimiento ejecutados por nuestro equipo.',
      },
      {
        step: 'PASO 5',
        title: 'Soporte Continuo',
        body: 'Contabilidad, nómina y planificación fiscal permanentes con un asesor dedicado.',
      },
    ],
  },
  formation: {
    eyebrow: 'Constitución remota',
    heading: 'Abre Tu Empresa en EE. UU. Totalmente en Línea',
    body: 'Nuestro equipo gestiona cada paso del proceso, desde el registro de la empresa hasta la obtención de tu EIN y la apertura de cuentas bancarias empresariales, permitiendo a los emprendedores establecer sus compañías de forma remota con confianza y eficiencia.',
    cta: 'Comenzar Hoy',
    imageAlt: 'Dos profesionales firmando documentos de constitución de empresa',
    badgeLabel: 'Firmado a distancia',
    badgeBody: 'Cero documentos por correo. Cero vuelos reservados.',
  },
  asset: {
    eyebrow: 'Patrimonio y estructura',
    heading: 'Protege lo Que Construiste y Estructúralo para Crecer',
    body: 'Los fundadores e inversionistas con patrimonio significativo necesitan más que una entidad. Diseñamos la arquitectura legal y fiscal que mantiene el patrimonio separado, eficiente y transferible.',
    pillars: [
      {
        title: 'Protección de Activos',
        body: 'Aísla el patrimonio personal y familiar de la responsabilidad del negocio con entidades en capas.',
      },
      {
        title: 'Optimización Fiscal',
        body: 'Reduce legalmente tu tasa efectiva con la entidad correcta y planificación de tratados.',
      },
      {
        title: 'Holdings',
        body: 'Consolida subsidiarias bajo un holding para un control y una salida más limpios.',
      },
      {
        title: 'Fideicomisos',
        body: 'Fideicomisos nacionales e internacionales diseñados para privacidad y sucesión.',
      },
      {
        title: 'Estrategia Patrimonial a Largo Plazo',
        body: 'Un plan multigeneracional revisado cada año conforme crece tu portafolio.',
      },
    ],
    vaultAlt: 'Puerta de bóveda de acero moderna en un interior oscuro de lujo',
    entrepreneurAlt: 'Emprendedor de pie junto a la ventana de un rascacielos con vista a la ciudad',
    officeAlt: 'Interior de una suite de oficinas ejecutivas de lujo',
  },
  outsourcing: {
    eyebrow: 'Tercerización',
    heading: 'Enfócate en Crecer Mientras Manejamos Tus Finanzas',
    subtitle:
      'Un departamento financiero tercerizado completo — libros conciliados, equipos pagados e informes con los que sí puedes decidir.',
    items: [
      { title: 'Contabilidad', body: 'Contabilidad mensual conciliada y cerrada a tiempo.' },
      { title: 'Nómina', body: 'Empleados y contratistas pagados, declarados y documentados.' },
      {
        title: 'Cumplimiento',
        body: 'Informes anuales, agente registrado y obligaciones estatales bajo control.',
      },
      { title: 'Informes Financieros', body: 'Estado de resultados, balance y flujo de caja accionables.' },
      { title: 'Soporte Mensual', body: 'Una llamada fija con tu asesor para revisar los números.' },
    ],
    imageAlt: 'Ilustración de un panel financiero con flujo de caja, nómina y cumplimiento',
  },
  tax: {
    eyebrow: 'Servicios fiscales',
    heading: 'Planificación Fiscal Que Te Ahorra Tiempo y Dinero',
    body: 'Declarar es la fecha límite, no la estrategia. Mapeamos tus obligaciones federales, estatales y transfronterizas, y planificamos el año para que tu tasa efectiva sea una decisión, no una sorpresa.',
    items: [
      'Cumplimiento del IRS',
      'Impuestos Personales',
      'Impuestos Corporativos',
      'Enmiendas',
      'ITIN',
      'Planificación Fiscal Estratégica',
    ],
    cta: 'Revisar Mi Situación Fiscal',
    imageAlt: 'Asesor fiscal revisando documentos financieros con un cliente',
    badgeLabel: 'Promedio por cliente',
    badgeValue: '$18,400',
    badgeBody: 'Ahorro fiscal anual identificado en el primer ciclo de planificación.',
  },
  testimonials: {
    eyebrow: 'Testimonios',
    heading: 'La Confianza de Fundadores e Inversionistas de Toda América',
    ratingLabel: 'Calificación de 5 sobre 5',
    items: [
      {
        quote: 'Abrir nuestra empresa en EE. UU. fue más fácil de lo esperado.',
        name: 'Mariana Duarte',
        role: 'Fundadora, SaaS de Logística',
        location: 'São Paulo, BR',
      },
      {
        quote: 'Excelente comunicación y orientación profesional.',
        name: 'Andrés Villalba',
        role: 'Socio Director',
        location: 'Bogotá, CO',
      },
      {
        quote: 'Su planificación fiscal nos ahorró miles.',
        name: 'Rachel Kim',
        role: 'CFO, Grupo de E-commerce',
        location: 'Miami, FL',
      },
      {
        quote: 'Rápidos, transparentes y confiables.',
        name: 'Diego Fuentes',
        role: 'Inversionista Inmobiliario',
        location: 'Ciudad de México, MX',
      },
      {
        quote: 'Se convirtieron en nuestro socio estratégico a largo plazo.',
        name: 'Sofía Ramírez',
        role: 'COO, Fintech',
        location: 'Buenos Aires, AR',
      },
      {
        quote: 'Totalmente recomendados.',
        name: 'Thomas Whitfield',
        role: 'Inversionista Ángel',
        location: 'Nueva York, NY',
      },
    ],
  },
  faq: {
    eyebrow: 'Preguntas frecuentes',
    heading: 'Lo Que los Fundadores Nos Preguntan Primero',
    body: '¿Aún tienes dudas? Tráelas a la consulta gratuita — sin compromiso y sin guion de ventas.',
    items: [
      {
        question: '¿Cuánto tiempo toma la constitución?',
        answer:
          'La mayoría de las entidades se registran en 24 a 72 horas según el estado. El EIN suele llegar en 1 a 3 semanas, y la cuenta bancaria puede abrirse en cuanto se emite.',
      },
      {
        question: '¿Todo se puede hacer de forma remota?',
        answer:
          'Sí. Constitución, EIN, banca, ITIN y cada presentación continua se gestionan 100% en línea. Los documentos se firman electrónicamente y recibes un expediente digital seguro con todo.',
      },
      {
        question: '¿Necesito viajar a Estados Unidos?',
        answer:
          'No es necesario viajar. Nuestro proceso está diseñado para fundadores e inversionistas no residentes, incluyendo bancos aliados que abren cuentas sin visitar EE. UU.',
      },
      {
        question: '¿Pueden ayudarme con los impuestos cada año?',
        answer:
          'Presentamos las declaraciones federales, estatales e informativas cada año y planificamos cada trimestre para que no haya sorpresas en abril.',
      },
      {
        question: '¿Ofrecen contabilidad después de la constitución?',
        answer:
          'Sí. Contabilidad, nómina, calendarios de cumplimiento e informes financieros mensuales están disponibles como servicio continuo con un asesor dedicado.',
      },
    ],
  },
  finalCta: {
    heading: '¿Listo para Hacer Crecer Tu Negocio en Estados Unidos?',
    subtitle: 'Agenda una consulta gratuita y recibe una estrategia personalizada para tu negocio.',
    assurances: ['Llamada gratuita de 30 minutos', 'Sin compromiso', 'Respuesta en 24 horas'],
  },
  form: {
    heading: 'Agendar Consulta',
    name: 'Nombre completo',
    namePlaceholder: 'Juana Pérez',
    email: 'Correo electrónico',
    emailPlaceholder: 'tu@empresa.com',
    phone: 'Teléfono (opcional)',
    phonePlaceholder: '+1 (305) 555-0142',
    goal: '¿En qué necesitas ayuda?',
    goalPlaceholder: 'Selecciona una opción',
    goalOptions: [
      'Abrir una empresa en EE. UU.',
      'Protección de activos y holdings',
      'Contabilidad y nómina',
      'Planificación y declaraciones fiscales',
      'Otro tema',
    ],
    submit: 'Agendar Consulta',
    submitting: 'Enviando…',
    privacy: 'Respondemos en 24 horas. Tu información nunca se comparte.',
    errors: {
      name: 'Por favor ingresa tu nombre completo.',
      email: 'Por favor ingresa tu correo electrónico.',
      emailInvalid: 'Por favor ingresa un correo electrónico válido.',
      goal: 'Por favor selecciona en qué necesitas ayuda.',
      generic: 'Corrige los campos señalados para continuar.',
    },
    success: {
      title: 'Recibimos tu solicitud de consulta.',
      body: 'Un asesor te contactará en 24 horas con los horarios disponibles y los siguientes pasos.',
      again: 'Enviar otra solicitud',
    },
  },
  footer: {
    tagline:
      'Consultoría legal, financiera y fiscal para emprendedores e inversionistas que construyen empresas en Estados Unidos.',
    floridaOffice: 'Oficina de Florida',
    floridaAddress: ['261 N University Dr Ste 500', 'Plantation, Florida 33324'],
    newYorkOffice: 'Oficina de Nueva York',
    newYorkAddress: ['418 Broadway Ste Y', 'Albany, New York 12207'],
    contact: 'Contacto',
    explore: 'Explorar',
    teamPhotoAlt: 'Socios de Standard Consulting Advice en un evento del sector en Las Vegas',
    freeConsultation: 'Consulta Gratuita',
    rights: 'Standard Consulting Advice. Todos los derechos reservados.',
    offices: 'Oficinas en Florida y Nueva York · Atendiendo EE. UU. + LATAM',
  },
}
